import React, { useState } from 'react';
import { base44 } from '@/api/base44Client';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import ConfirmableDialog from '@/components/common/ConfirmableDialog';
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import SearchableTagSelect from '@/components/common/SearchableTagSelect';
import { Activity, Target, Plus, Trash2, AlertCircle } from 'lucide-react';

export default function RockActivityCreator({ open, onClose, issueId, officers }) {
  const queryClient = useQueryClient();

  const { data: user } = useQuery({
    queryKey: ['me'],
    queryFn: () => base44.auth.me()
  });

  const { data: profile } = useQuery({
    queryKey: ['myProfile'],
    queryFn: async () => {
      const profiles = await base44.entities.OfficerProfile.list();
      return profiles.find(p => p.user_email === user?.email);
    },
    enabled: !!user
  });

  const isCommander = user?.role === 'admin' || profile?.custom_role === 'Commander';

  const { data: missions = [] } = useQuery({
    queryKey: ['missions'],
    queryFn: () => base44.entities.Mission.list(),
    initialData: []
  });

  const { data: objectives = [] } = useQuery({
    queryKey: ['objectives'],
    queryFn: () => base44.entities.Objective.list(),
    initialData: []
  });

  const { data: activities = [] } = useQuery({
    queryKey: ['activities'],
    queryFn: () => base44.entities.Activity.list(),
    initialData: []
  });

  const [form, setForm] = useState({
    mission_id: '',
    objective_id: '',
    title: '',
    description: '',
    responsible_email: '',
    eisenhower_quadrant: 'urgent_important',
    key_results: [],
    due_date: '',
  });
  const [formErrors, setFormErrors] = useState([]);

  const availableObjectives = form.mission_id
    ? objectives.filter(o => o.mission_id === form.mission_id && o.status === 'active')
    : [];

  const createMutation = useMutation({
    mutationFn: async (data) => {
      const objective = objectives.find(o => o.id === data.objective_id);
      const objectiveActivities = activities.filter(a => a.objective_id === data.objective_id);
      const maxNum = objectiveActivities.reduce((max, a) => {
        if (a.activity_code) {
          const match = a.activity_code.match(/-A(\d+)$/);
          if (match) return Math.max(max, parseInt(match[1], 10));
        }
        return max;
      }, 0);
      const activity_code = `${objective?.objective_code || 'UNK'}-A${maxNum + 1}`;
      const accountable_email = objective?.owner_email;
      if (!accountable_email) throw new Error('Objective has no owner');

      let key_results_progress = 0;
      if (data.key_results?.length > 0) {
        const total = data.key_results.reduce((sum, kr) => {
          const c = parseFloat(kr.current_value) || 0;
          const t = parseFloat(kr.target) || 1;
          return sum + (t > 0 ? (c / t) * 100 : 0);
        }, 0);
        key_results_progress = Math.round(total / data.key_results.length);
      }

      return base44.entities.Activity.create({
        ...data,
        activity_code,
        accountable_email,
        status: 'active',
        status_flag: 'yellow',
        key_results: data.key_results || [],
        key_results_progress
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['activities'] });
      handleClose();
    },
    onError: (err) => alert(err.message)
  });

  const handleClose = () => {
    setForm({
      mission_id: '', objective_id: '', title: '', description: '',
      responsible_email: '', eisenhower_quadrant: 'urgent_important',
      key_results: [], due_date: ''
    });
    setFormErrors([]);
    onClose();
  };

  const validate = () => {
    const errs = [];
    if (!form.mission_id) errs.push('Mission is required');
    if (!form.objective_id) errs.push('Objective is required');
    if (!form.title?.trim()) errs.push('Title is required');
    if (!form.responsible_email) errs.push('Responsible officer is required');
    if (!form.due_date) errs.push('Due date is required');
    if (!form.key_results?.length) errs.push('At least one key result is required');
    return errs;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const errs = validate();
    if (errs.length > 0) { setFormErrors(errs); return; }
    setFormErrors([]);
    createMutation.mutate(form);
  };

  const addKeyResult = () => setForm(prev => ({
    ...prev,
    key_results: [...(prev.key_results || []), { description: '', target: '', current_value: '0', unit: '' }]
  }));

  const updateKeyResult = (idx, field, val) => {
    const updated = [...form.key_results];
    updated[idx] = { ...updated[idx], [field]: val };
    setForm(prev => ({ ...prev, key_results: updated }));
  };

  const removeKeyResult = (idx) => setForm(prev => ({
    ...prev,
    key_results: prev.key_results.filter((_, i) => i !== idx)
  }));

  const eisenhowerLabels = {
    urgent_important: 'Do First',
    not_urgent_important: 'Schedule',
    urgent_not_important: 'Delegate',
    not_urgent_not_important: 'Eliminate'
  };

  return (
    <ConfirmableDialog open={open} onOpenChange={(o) => !o && handleClose()}>
      <DialogContent data-testid="issues-rockactivitycreator-dialog" className="bg-slate-900 border-slate-800 text-slate-100 max-w-3xl w-[95vw] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Activity className="w-5 h-5 text-amber-400" />
            Create Activity (Rock Resolution)
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4 mt-4">
          <div className="space-y-2">
            <Label>Mission</Label>
            <SearchableTagSelect
              options={missions.filter(m => m.id).map(m => ({
                value: m.id,
                label: `${m.mission_code ? `[${m.mission_code}] ` : ''}${m.title}`
              }))}
              value={form.mission_id}
              onValueChange={(v) => setForm(prev => ({ ...prev, mission_id: v, objective_id: '' }))}
              placeholder="Select mission..."
              emptyMessage="No missions found"
              className="bg-slate-950 border-slate-800"
            />
          </div>

          <div className="space-y-2">
            <Label>Objective</Label>
            <SearchableTagSelect
              options={availableObjectives.filter(o => o.id).map(o => ({
                value: o.id,
                label: `${o.objective_code ? `[${o.objective_code}] ` : ''}${o.statement}`
              }))}
              value={form.objective_id}
              onValueChange={(v) => setForm(prev => ({ ...prev, objective_id: v }))}
              placeholder={form.mission_id ? 'Select objective...' : 'Select mission first...'}
              emptyMessage="No active objectives found"
              disabled={!form.mission_id}
              className="bg-slate-950 border-slate-800"
            />
          </div>

          <div className="space-y-2">
            <Label>Activity Title</Label>
            <Textarea data-testid="issues-rockactivitycreator-textarea"
              value={form.title}
              onChange={(e) => setForm(prev => ({ ...prev, title: e.target.value }))}
              className="bg-white border-slate-300 text-slate-900 placeholder:text-slate-500 min-h-[44px] resize-none"
              placeholder="Describe the activity..."
            />
          </div>

          <div className="space-y-2">
            <Label>Description</Label>
            <Textarea data-testid="issues-rockactivitycreator-textarea-1"
              value={form.description}
              onChange={(e) => setForm(prev => ({ ...prev, description: e.target.value }))}
              className="bg-white border-slate-300 text-slate-900 placeholder:text-slate-500 min-h-[60px] resize-none"
              placeholder="Additional details..."
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Responsible (R)</Label>
              <Select value={form.responsible_email} onValueChange={(v) => setForm(prev => ({ ...prev, responsible_email: v }))}>
                <SelectTrigger className="bg-slate-950 border-slate-800"><SelectValue placeholder="Select officer..." /></SelectTrigger>
                <SelectContent>
                  {(officers || []).filter(o => o.user_email && !o.is_bot).map(o => (
                    <SelectItem key={o.id} value={o.user_email}>
                      {o.callsign}, {o.rank_title}, L{o.clearance_level}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Priority</Label>
              <Select value={form.eisenhower_quadrant} onValueChange={(v) => setForm(prev => ({ ...prev, eisenhower_quadrant: v }))}>
                <SelectTrigger className="bg-slate-950 border-slate-800"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.entries(eisenhowerLabels).map(([val, label]) => (
                    <SelectItem key={val} value={val}>{label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-2">
            <Label>Due Date</Label>
            <Input data-testid="issues-rockactivitycreator-input"
              type="date"
              value={form.due_date}
              onChange={(e) => setForm(prev => ({ ...prev, due_date: e.target.value }))}
              className="bg-white border-slate-300 text-slate-900"
            />
          </div>

          {/* Key Results */}
          <div className="space-y-3 p-4 bg-emerald-500/5 rounded-xl border border-emerald-500/20">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Target className="w-4 h-4 text-emerald-400" />
                <Label className="text-sm font-bold text-emerald-300">Key Results ({(form.key_results || []).length})</Label>
              </div>
              <Button data-testid="issues-add-btn" type="button" size="sm" variant="outline" onClick={addKeyResult} className="border-emerald-500/30 text-emerald-400 hover:bg-emerald-500/10">
                <Plus className="w-3 h-3 mr-1" /> Add
              </Button>
            </div>
            {(form.key_results || []).length === 0 && (
              <button type="button" onClick={addKeyResult} className="w-full p-3 border-2 border-dashed border-slate-700 rounded-lg text-slate-500 hover:border-emerald-500/50 hover:text-emerald-400 transition-colors text-sm flex items-center justify-center gap-2">
                <Plus className="w-4 h-4" /> Add first key result
              </button>
            )}
            {(form.key_results || []).map((kr, idx) => (
              <div key={idx} className="p-3 bg-slate-950 rounded-lg border border-slate-800 space-y-2">
                <div className="flex justify-between items-center">
                  <span className="text-xs text-slate-500">KR #{idx + 1}</span>
                  <Button type="button" size="icon" variant="ghost" onClick={() => removeKeyResult(idx)} className="h-6 w-6">
                    <Trash2 className="w-3 h-3 text-red-400" />
                  </Button>
                </div>
                <Input data-testid="issues-rockactivitycreator-input-1"
                  placeholder="Key result description"
                  value={kr.description}
                  onChange={(e) => updateKeyResult(idx, 'description', e.target.value)}
                  className="bg-white border-slate-300 text-slate-900 placeholder:text-slate-500"
                />
                <div className="grid grid-cols-3 gap-2">
                  <Input data-testid="issues-target-input" type="number" placeholder="Target" value={kr.target} onChange={(e) => updateKeyResult(idx, 'target', e.target.value)} className="bg-white border-slate-300 text-slate-900 text-sm" />
                  <Input data-testid="issues-current-input" type="number" placeholder="Current" value={kr.current_value} onChange={(e) => updateKeyResult(idx, 'current_value', e.target.value)} className="bg-white border-slate-300 text-slate-900 text-sm" />
                  <Input data-testid="issues-unit-input" placeholder="Unit" value={kr.unit} onChange={(e) => updateKeyResult(idx, 'unit', e.target.value)} className="bg-white border-slate-300 text-slate-900 text-sm" />
                </div>
              </div>
            ))}
          </div>

          {formErrors.length > 0 && (
            <div className="p-3 bg-red-500/10 border border-red-500/30 rounded-lg space-y-1">
              <div className="text-sm font-semibold text-red-400">Please fix:</div>
              {formErrors.map((err, idx) => (
                <div key={idx} className="text-xs text-red-300 flex items-center gap-1">
                  <AlertCircle className="w-3 h-3 flex-shrink-0" /> {err}
                </div>
              ))}
            </div>
          )}

          <Button type="submit" disabled={createMutation.isPending} className="w-full bg-indigo-600 hover:bg-indigo-700">
            {createMutation.isPending ? 'Creating...' : 'Create Activity'}
          </Button>
        </form>
      </DialogContent>
    </ConfirmableDialog>
  );
}